// The agency console pages (M5-05; agency users only; anyone else gets the not-found page and no
// API call, as the API answers them 404):
// - `/operator/approvals?p=&sla=`: approvals SLA;
// - `/operator/supervisors?p=`: supervisor load;
// - `/operator/cost?month=&view=`: cost to serve per DE, client and template;
// - `/operator/break-glass`: break-glass requests, approvals and live sessions.
import { createRoute, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import type { Api } from "../api/client";
import type { MeResponse } from "../api/types";
import { monthKey, SLA_DEFAULT } from "../features/agency/agency-queries";
import { ApprovalsSlaPage } from "../features/agency/approvals-sla-page";
import { BreakGlassPage } from "../features/agency/break-glass-page";
import { COST_VIEWS, CostToServePage, type CostView } from "../features/agency/cost-to-serve-page";
import { SupervisorLoadPage } from "../features/agency/supervisor-load-page";
import { isOperator } from "../features/company";
import { useSession } from "../features/use-feature";
import { validateFocusSearch } from "../focus/focus";
import { currentWeek } from "../focus/weeks";
import { NotFoundPage } from "../pages/status-pages";
import { OperatorShell, PlainShell } from "../shell/app-shell";
import { authedRoute } from "./_authed";

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** The operator shell for agency users; the not-found page (and no call) for anyone else. */
function OperatorOnly({ api, children }: { api: Api; children: (me: MeResponse) => ReactNode }) {
  const { me } = useSession();
  if (!isOperator(me))
    return (
      <PlainShell me={me} api={api} current={null}>
        <NotFoundPage />
      </PlainShell>
    );
  return (
    <OperatorShell me={me} api={api}>
      {children(me)}
    </OperatorShell>
  );
}

const weekSearch = (search: Record<string, unknown>): { p?: string } => {
  const { p } = validateFocusSearch(search);
  return p ? { p } : {};
};

export const operatorApprovalsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/approvals",
  validateSearch: (search: Record<string, unknown>): { p?: string; sla?: number } => {
    const sla = Number(search.sla);
    return {
      ...weekSearch(search),
      ...(Number.isInteger(sla) && sla >= 15 && sla <= 10_080 ? { sla } : {}),
    };
  },
  component: function ApprovalsScreen() {
    const { api } = operatorApprovalsRoute.useRouteContext();
    const { p, sla } = operatorApprovalsRoute.useSearch();
    const navigate = useNavigate();
    return (
      <OperatorOnly api={api}>
        {() => (
          <ApprovalsSlaPage
            api={api}
            week={p ?? currentWeek()}
            sla={sla ?? SLA_DEFAULT}
            onChange={(next) =>
              void navigate({
                to: "/operator/approvals",
                search: {
                  p: next.week,
                  ...(next.sla !== SLA_DEFAULT ? { sla: next.sla } : {}),
                },
              })
            }
          />
        )}
      </OperatorOnly>
    );
  },
});

export const operatorSupervisorsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/supervisors",
  validateSearch: weekSearch,
  component: function SupervisorsScreen() {
    const { api } = operatorSupervisorsRoute.useRouteContext();
    const { p } = operatorSupervisorsRoute.useSearch();
    const navigate = useNavigate();
    return (
      <OperatorOnly api={api}>
        {() => (
          <SupervisorLoadPage
            api={api}
            week={p ?? currentWeek()}
            onWeek={(week) => void navigate({ to: "/operator/supervisors", search: { p: week } })}
          />
        )}
      </OperatorOnly>
    );
  },
});

export const operatorCostRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/cost",
  validateSearch: (search: Record<string, unknown>): { month?: string; view?: CostView } => ({
    ...(typeof search.month === "string" && MONTH.test(search.month)
      ? { month: search.month }
      : {}),
    ...(COST_VIEWS.includes(search.view as CostView) ? { view: search.view as CostView } : {}),
  }),
  component: function CostScreen() {
    const { api } = operatorCostRoute.useRouteContext();
    const { month, view } = operatorCostRoute.useSearch();
    const navigate = useNavigate();
    return (
      <OperatorOnly api={api}>
        {() => (
          <CostToServePage
            api={api}
            month={month ?? monthKey(new Date())}
            view={view ?? "des"}
            onChange={(next) =>
              void navigate({
                to: "/operator/cost",
                search: { month: next.month, ...(next.view !== "des" ? { view: next.view } : {}) },
              })
            }
          />
        )}
      </OperatorOnly>
    );
  },
});

export const operatorBreakGlassRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/break-glass",
  component: function BreakGlassScreen() {
    const { api } = operatorBreakGlassRoute.useRouteContext();
    return <OperatorOnly api={api}>{(me) => <BreakGlassPage api={api} me={me} />}</OperatorOnly>;
  },
});

export const agencyOperatorRoutes = [
  operatorApprovalsRoute,
  operatorSupervisorsRoute,
  operatorCostRoute,
  operatorBreakGlassRoute,
] as const;
